/**
 * StepDataBiz — Etapa: Data Sources (Negócio).
 *
 * Mostra as fontes de dados de negócio do projeto e permite adicionar novas.
 * Persistência real via POST /projects/{id}/data-sources (kind: 'business').
 *
 * Paleta C, estilos inline, pt-BR, a11y preservados.
 * Accent: digital (igual ao protótipo para indexação de negócio).
 */
import { useState } from 'react';
import { Database, Plus, ArrowRight, CheckCircle2, Sparkles, Upload, Link2 } from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, Badge } from '@/components/ui';
import { MutationError, QueryError, LoadingSpinner } from '@/components/ui/QueryState';
import { useDataSources, useAddDataSource, useAddProjectDocument, useAddDocumentFromUrl, useSuggestBusinessScope } from '@/api/projects';
import { AiProgress } from '@/components/ui/AiProgress';
import { useJourney } from '../JourneyContext';

const SOURCE_TYPES = [
  { value: 'confluence',  label: 'Confluence' },
  { value: 'notion',      label: 'Notion' },
  { value: 'jira',        label: 'Jira' },
  { value: 'sharepoint',  label: 'SharePoint' },
  { value: 'swagger',     label: 'Swagger / OpenAPI' },
  { value: 'upload',      label: 'Upload' },
  { value: 'outros',      label: 'Outros' },
];

// Extensões aceitas para "entender o negócio a partir de APIs" (Swagger/OpenAPI) e docs.
const API_DOC_EXT = ['.json', '.yaml', '.yml', '.md', '.txt'];

function SourceIcon({ type }) {
  return (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: 6,
        backgroundColor: `${C.digital}22`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Database size={14} style={{ color: C.digital }} aria-hidden="true" />
    </div>
  );
}

export function StepDataBiz({ onNext, onBack }) {
  const { projectId, deliveryScope } = useJourney();
  const isAnalise = deliveryScope === 'analise';

  const sourcesQuery = useDataSources(projectId);
  const addSource    = useAddDataSource(projectId);
  const addDoc       = useAddProjectDocument(projectId);
  const addFromUrl   = useAddDocumentFromUrl(projectId);
  const suggestScope = useSuggestBusinessScope(projectId);

  const [name,    setName]    = useState('');
  const [type,    setType]    = useState('confluence');
  const [docCount, setDocCount] = useState('');
  const [apiUrl,  setApiUrl]  = useState('');
  const [uploadMsg, setUploadMsg] = useState('');

  async function handleSwaggerFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadMsg('');
    if (!API_DOC_EXT.some((ext) => file.name.toLowerCase().endsWith(ext))) {
      setUploadMsg(`Formato não suportado. Aceitos: ${API_DOC_EXT.join(', ')}`);
      return;
    }
    try {
      const content = await file.text();
      await addDoc.mutateAsync({
        filename: file.name,
        doc_tag: 'architecture',           // Swagger/OpenAPI = contrato de arquitetura
        content_text: content.slice(0, 1_000_000),
        content_type: file.type || 'text/plain',
        size_bytes: content.length,
      });
      setUploadMsg(`✓ "${file.name}" enviado e sendo indexado (RAG).`);
    } catch { setUploadMsg('Falha ao enviar o arquivo.'); }
  }

  async function handleAddFromUrl() {
    if (!apiUrl.trim()) return;
    setUploadMsg('');
    try {
      await addFromUrl.mutateAsync({ url: apiUrl.trim(), doc_tag: 'architecture' });
      setUploadMsg(`✓ URL ingerida e sendo indexada (RAG).`);
      setApiUrl('');
    } catch { /* erro exibido pelo MutationError */ }
  }

  const bizSources = (sourcesQuery.data ?? []).filter(
    (s) => !s.kind || s.kind === 'business',
  );

  async function handleAdd(e) {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await addSource.mutateAsync({
        name:      name.trim(),
        type,
        kind:      'business',
        doc_count: docCount ? parseInt(docCount, 10) : undefined,
      });
      setName('');
      setDocCount('');
    } catch { /* tratado pelo MutationError */ }
  }

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: C.neutral, margin: '0 0 6px' }}>
          Data Sources — Negócio
        </h2>
        <p style={{ fontSize: 14, color: C.neutralDim, margin: 0 }}>
          Fontes de documentação de negócio indexadas com embeddings{' '}
          <span style={{ color: C.digital }}>voyage-3</span> para RAG semântico.
        </p>
      </div>

      {sourcesQuery.isLoading && <LoadingSpinner label="Carregando data sources..." />}
      {sourcesQuery.error && (
        <QueryError error={sourcesQuery.error} onRetry={() => sourcesQuery.refetch()} />
      )}

      {/* Fontes existentes */}
      {bizSources.length > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.neutralDim, marginBottom: 12, letterSpacing: '0.04em' }}>
            FONTES CADASTRADAS
          </div>
          {bizSources.map((s, i) => (
            <div
              key={s.id ?? i}
              className="flex items-center gap-3 py-3"
              style={{ borderBottom: i < bizSources.length - 1 ? `1px solid ${C.blackBorder}` : 'none' }}
            >
              <SourceIcon type={s.type} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: C.neutral }}>{s.name}</div>
                <div style={{ fontSize: 12, color: C.neutralMuted }}>
                  {s.type && <span style={{ textTransform: 'uppercase' }}>{s.type}</span>}
                  {s.doc_count && ` · ${s.doc_count} docs`}
                </div>
              </div>
              {s.status === 'indexed' && (
                <div className="flex items-center gap-1">
                  <CheckCircle2 size={13} style={{ color: C.cyber }} aria-hidden="true" />
                  <Badge color={C.cyber} light>INDEXADO</Badge>
                </div>
              )}
              {s.status === 'pending' && (
                <Badge color={C.digital} light>PENDENTE</Badge>
              )}
            </div>
          ))}
        </Card>
      )}

      {/* Adicionar fonte */}
      <Card style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.neutralDim, marginBottom: 16, letterSpacing: '0.04em' }}>
          ADICIONAR FONTE DE DADOS
        </div>
        <MutationError error={addSource.error} onDismiss={() => addSource.reset()} />
        <form onSubmit={handleAdd}>
          <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 140px 100px auto' }}>
            <div>
              <label
                htmlFor="ds-name"
                style={{ display: 'block', fontSize: 12, color: C.neutralDim, marginBottom: 6 }}
              >
                Nome da fonte
              </label>
              <input
                id="ds-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex.: Confluence — Docs do Core"
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  backgroundColor: C.black,
                  border: `1px solid ${C.blackBorder}`,
                  borderRadius: 6,
                  color: C.neutral,
                  fontSize: 13,
                }}
              />
            </div>
            <div>
              <label
                htmlFor="ds-type"
                style={{ display: 'block', fontSize: 12, color: C.neutralDim, marginBottom: 6 }}
              >
                Tipo
              </label>
              <select
                id="ds-type"
                value={type}
                onChange={(e) => setType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  backgroundColor: C.black,
                  border: `1px solid ${C.blackBorder}`,
                  borderRadius: 6,
                  color: C.neutral,
                  fontSize: 13,
                }}
              >
                {SOURCE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="ds-count"
                style={{ display: 'block', fontSize: 12, color: C.neutralDim, marginBottom: 6 }}
              >
                Qtd. docs
              </label>
              <input
                id="ds-count"
                type="number"
                value={docCount}
                onChange={(e) => setDocCount(e.target.value)}
                min={0}
                placeholder="—"
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  backgroundColor: C.black,
                  border: `1px solid ${C.blackBorder}`,
                  borderRadius: 6,
                  color: C.neutral,
                  fontSize: 13,
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Btn
                type="submit"
                variant="outline"
                size="sm"
                icon={Plus}
                disabled={!name.trim() || addSource.isPending}
                aria-busy={addSource.isPending}
              >
                {addSource.isPending ? '...' : 'Adicionar'}
              </Btn>
            </div>
          </div>
        </form>
      </Card>

      {/* Entender o negócio a partir de APIs (Swagger/OpenAPI) — arquivo ou URL — + IA */}
      <Card style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.neutralDim, marginBottom: 6, letterSpacing: '0.04em' }}>
          ENTENDER O NEGÓCIO A PARTIR DE APIs / DOCUMENTOS
        </div>
        <p style={{ fontSize: 12.5, color: C.neutralMuted, margin: '0 0 14px', lineHeight: 1.5 }}>
          Suba um <strong style={{ color: C.neutral }}>Swagger/OpenAPI</strong> (ou docs) por arquivo ou URL — o
          conteúdo é indexado (RAG) e a IA infere as capacidades de negócio a partir das APIs.
        </p>

        <MutationError error={addDoc.error || addFromUrl.error} onDismiss={() => { addDoc.reset(); addFromUrl.reset(); }} />

        <div className="flex items-center gap-3" style={{ flexWrap: 'wrap', marginBottom: 12 }}>
          {/* Upload por arquivo */}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
            padding: '9px 14px', borderRadius: 6, border: `1px solid ${C.blackBorder}`,
            backgroundColor: C.black, color: C.neutral, fontSize: 13 }}>
            <Upload size={14} style={{ color: C.digital }} aria-hidden="true" />
            {addDoc.isPending ? 'Enviando...' : 'Subir arquivo (.json/.yaml/.md)'}
            <input type="file" accept={API_DOC_EXT.join(',')} onChange={handleSwaggerFile}
              disabled={addDoc.isPending} style={{ display: 'none' }} />
          </label>

          {/* Ingestão por URL */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 280 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Link2 size={14} style={{ position: 'absolute', left: 10, top: 11, color: C.neutralMuted }} aria-hidden="true" />
              <input
                type="url" value={apiUrl} onChange={(e) => setApiUrl(e.target.value)}
                placeholder="https://.../swagger.json  ou  /openapi.yaml"
                style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px 9px 32px',
                  backgroundColor: C.black, border: `1px solid ${C.blackBorder}`, borderRadius: 6,
                  color: C.neutral, fontSize: 13 }} />
            </div>
            <Btn variant="outline" size="sm" onClick={handleAddFromUrl}
              disabled={!apiUrl.trim() || addFromUrl.isPending} aria-busy={addFromUrl.isPending}>
              {addFromUrl.isPending ? '...' : 'Ingerir URL'}
            </Btn>
          </div>
        </div>

        {uploadMsg && (
          <div role="status" style={{ fontSize: 12.5, color: uploadMsg.startsWith('✓') ? C.cyber : C.digital, marginBottom: 12 }}>
            {uploadMsg}
          </div>
        )}

        <div style={{ borderTop: `1px solid ${C.blackBorder}`, paddingTop: 14 }}>
          <div className="flex items-center gap-3" style={{ flexWrap: 'wrap' }}>
            <Btn variant="outline" size="sm" icon={Sparkles}
              onClick={() => suggestScope.mutate()} disabled={suggestScope.isPending} aria-busy={suggestScope.isPending}>
              {suggestScope.isPending ? 'Sugerindo com IA...' : 'Sugerir escopo de negócio com IA'}
            </Btn>
            <span style={{ fontSize: 12, color: C.neutralMuted }}>
              preserva a Ideação e enriquece com as fontes indexadas (documentos, Swagger, discovery)
            </span>
          </div>
          <AiProgress busy={suggestScope.isPending} label="Sugerindo escopo de negócio com IA" />
          <MutationError error={suggestScope.error} onDismiss={() => suggestScope.reset()} />
          {suggestScope.data?.suggestion && (
            <div style={{ marginTop: 12, padding: 14, borderRadius: 8, backgroundColor: C.black,
              border: `1px solid ${C.blackBorder}`, whiteSpace: 'pre-wrap', fontSize: 13,
              color: C.neutral, lineHeight: 1.6, maxHeight: 320, overflow: 'auto' }}>
              {suggestScope.data.suggestion}
            </div>
          )}
        </div>
      </Card>

      {/* Nota */}
      <div
        style={{
          padding: '10px 14px',
          borderRadius: 6,
          backgroundColor: `${C.digital}08`,
          border: `1px solid ${C.blackBorder}`,
          fontSize: 12,
          color: C.neutralDim,
          marginBottom: 28,
          lineHeight: 1.5,
        }}
        role="note"
      >
        As fontes serão indexadas com embeddings{' '}
        <span style={{ color: C.digital }}>voyage-3</span> antes da execução do pipeline.
        Personas, entidades de domínio e termos do glossário são extraídos automaticamente.
      </div>

      {/* Navegação */}
      <div className="flex items-center justify-between">
        <Btn variant="ghost" size="md" onClick={onBack}>
          Voltar
        </Btn>
        <Btn variant="primary" size="lg" icon={ArrowRight} onClick={onNext}>
          {isAnalise ? 'Concluir configuração' : 'Continuar — Critérios Técnicos'}
        </Btn>
      </div>
    </div>
  );
}
